import type { ErrorLike } from '@apollo/client';
import { orderBy } from '@realadvisor/array';

import { gql } from '@graphql/__generated__';
import {
  type Activities_Arr_Rel_Insert_Input,
  Activity_Types_Enum_Enum,
  type BuyerLeadDnDColumnFragment,
  type Buyer_Leads_Bool_Exp,
  type GetBuyerLeadStagesQuery,
  type Property_Searches_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { getFullName } from '@modules/_shared/components/email-form/contextSuggestions';
import type { EmailFormRecipient } from '@modules/_shared/components/email-form/types';
import { addWhereClause } from '@modules/_shared/components/filters/Filters';
import type { KanbanColumnDefinition } from '@modules/_shared/components/kanban/KanbanBoard';
import { getStartOfDayOneYearAgo } from '@modules/_shared/utils/dates';
import { parseJsonParam } from '@modules/_shared/utils/listUrlParams';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import type { Translate } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';

export type BuyerLeadStage = GetBuyerLeadStagesQuery['lead_stages'][number];
type BuyerLeadKanbanStage = Pick<BuyerLeadStage, 'id' | 'label' | 'name'> & {
  status?: string | null;
};

type BuyerLeadKanbanColumnData = {
  id: string;
  label?: string | null;
  status?: string | null;
};
export type BuyerLeadKanbanStageOption = {
  id: string;
  label: string;
  status?: string | null;
};

export const getBuyerLeadKanbanStages = (
  stages: readonly BuyerLeadKanbanStage[] | null | undefined,
): BuyerLeadKanbanStageOption[] =>
  stages?.map(stage => ({
    id: stage.id,
    label: stage.label ?? '',
    status: stage.status,
  })) ?? [];

export const extractBuyerLeadKanbanItems = <
  BuyerLead extends { stage_id?: string | null; updated_at?: string | null },
>(
  buyerLeads: BuyerLead[],
  stageId: string,
): BuyerLead[] =>
  orderBy(
    buyerLeads.filter(lead => lead.stage_id === stageId),
    lead => lead.updated_at,
    'desc',
  );

export const createBuyerLeadKanbanColumnDefinitions = <
  BuyerLead extends { stage_id?: string | null; updated_at?: string | null },
>(
  stages: readonly BuyerLeadKanbanStage[] | null | undefined,
  render: KanbanColumnDefinition<
    BuyerLead,
    BuyerLeadKanbanColumnData
  >['render'],
): KanbanColumnDefinition<BuyerLead, BuyerLeadKanbanColumnData>[] =>
  stages?.map(stage => ({
    id: stage.id,
    data: {
      id: stage.id,
      label: stage.label,
      status: stage.status,
    },
    extractItems: extractBuyerLeadKanbanItems,
    render,
  })) ?? [];

export const buyerLeadKanbanReorderingDisabled = true;

export const hasBuyerLeadKanbanStageChanged = (
  buyerLead: { stage_id?: string | null },
  destinationStageId: string | number,
) => buyerLead.stage_id !== destinationStageId;

export const getBuyerLeadKanbanDisplayName = (buyerLead: {
  user: { first_name?: string | null; last_name?: string | null };
}) => `${buyerLead.user.first_name ?? ''} ${buyerLead.user.last_name ?? ''}`;

type BuyerLeadKanbanAnnouncementOperation =
  | {
      type: 'item-reorder';
      column: { label?: string | null };
      startIndex: number;
      finishIndex: number;
    }
  | {
      type: 'item-move';
      sourceColumn: { label?: string | null };
      destinationColumn: { label?: string | null };
      startIndex: number;
      finishIndex: number;
    };

export const getBuyerLeadKanbanLiveRegionAnnouncement = (
  operation: BuyerLeadKanbanAnnouncementOperation,
  buyerName: string,
  includeReorder = true,
): string | null => {
  const { finishIndex, startIndex } = operation;

  if (operation.type === 'item-move') {
    return `You've moved buyer ${buyerName} from position ${
      startIndex + 1
    } of the ${operation.sourceColumn.label} column to position ${
      finishIndex + 1
    } in the ${operation.destinationColumn.label} column.`;
  }

  if (includeReorder) {
    return `You've moved buyer ${buyerName} from position ${
      startIndex + 1
    } to position ${finishIndex + 1} in the ${operation.column.label} column.`;
  }

  return null;
};

type BuyerLeadEmailRecipientUser = NonNullable<
  BuyerLeadDnDColumnFragment['user']
>;

export const getBuyerLeadEmailRecipients = <BuyerLead extends { id: string }>(
  buyerLeads: readonly BuyerLead[],
  getUser: (buyerLead: BuyerLead) => BuyerLeadEmailRecipientUser | null,
): EmailFormRecipient[] =>
  buyerLeads.reduce<EmailFormRecipient[]>((recipients, buyerLead) => {
    const user = getUser(buyerLead);
    if (user == null) {
      return recipients;
    }

    const email =
      user.emails.find(candidate => candidate.primary)?.email ??
      user.emails[0]?.email;

    if (email != null) {
      recipients.push({
        id: user.id,
        email,
        name: getFullName(user) || undefined,
        buyerLeadId: toGlobalId('BuyerLead', buyerLead.id),
      });
    }

    return recipients;
  }, []);

export const getBuyerLeadParentGlobalIds = <BuyerLead extends { id: string }>(
  buyerLeads: readonly BuyerLead[],
) => buyerLeads.map(buyerLead => toGlobalId('BuyerLead', buyerLead.id));

export const getBuyerLeadEmailRecipientStatus = (
  buyerLeadsCount: number,
  recipientsCount: number,
  t: Translate,
) => ({
  hasMissingEmailRecipients: recipientsCount !== buyerLeadsCount,
  tooltipTitle:
    recipientsCount === 0
      ? t('None of the leads you have selected have an email.')
      : t(
          "Some of the leads you have selected don't have an email. They won't be included in the recipients.",
        ),
});

// Narrow shape consumed by `getDefaultBuyersMatchingFilters`. Both the
// `DefaultBuyersMatchingFilters` lots-fragment and the equivalent mls_lots
// query (cross-tenant) satisfy this structurally — declaring the input
// explicitly avoids unsafe casts at the MLS call site and keeps the contract
// honest if the lots-side fragment evolves.
export type DefaultBuyersMatchingFiltersInput = {
  offer_type?: string | null;
  sale_price?: number | null;
  rent_net?: number | null;
  price_unit?: string | null;
  property: {
    computed_surface?: number | null;
    locality_id?: string | null;
    places?: ReadonlyArray<{ id: string }> | null;
    property_type?: {
      composite_type_object?: { name?: string | null } | null;
    } | null;
  };
};

export const getBuyerLeadStageOptions = (stages: BuyerLeadStage[]) =>
  stages.map(stage => ({
    value: stage.id,
    label: stage.label ?? stage.name ?? '',
  }));

export const getBuyerLeadStageId = (
  stages: readonly BuyerLeadKanbanStage[] | null | undefined,
  name: string,
) => stages?.find(stage => stage.name === name)?.id;

export const getBuyerLeadErrorMessage = (error: ErrorLike, t: Translate) =>
  error.message.includes(
    'Uniqueness violation. duplicate key value violates unique constraint',
  )
    ? t('The buyer you are trying to add already exists!')
    : error.message;

export const buildOriginationActivity = (
  userId: string,
  createdBy?: string,
): Activities_Arr_Rel_Insert_Input => ({
  data: [
    {
      activity_type: Activity_Types_Enum_Enum.Origination,
      done: true,
      success: true,
      done_at: new Date().toISOString(),
      ...(createdBy != null && { created_by: createdBy }),
      activities_target_users: {
        data: [
          {
            target_user_id: userId,
          },
        ],
      },
    },
  ],
});

export const DEFAULT_BUYERS_MATCHING_FILTERS_FRAGMENT = gql(/* GraphQL */ `
  fragment DefaultBuyersMatchingFilters on lots {
    id
    offer_type
    sale_price
    rent_net
    price_unit
    property {
      id
      computed_surface
      locality_id
      places(where: { type: { _nin: [country, region] } }) {
        id
      }
      property_type {
        id
        composite_type_object {
          id
          name
        }
      }
    }
  }
`);

export const getDefaultBuyersMatchingFilters = (
  listing: DefaultBuyersMatchingFiltersInput,
  extendedPlaces?: string[],
): Property_Searches_Bool_Exp => {
  let where: Property_Searches_Bool_Exp = {
    created_at: { _gte: getStartOfDayOneYearAgo().toISOString() },
  };

  // build prefilled filters
  if (listing.offer_type != null) {
    if (listing.sale_price != null) {
      // +50% rounded to the nearest 50000
      where = addWhereClause(
        where,
        ['property_searches_bool_exp', 'computed_budget_lte', '_lte'],
        Math.round((listing.sale_price * 1.5) / 50000) * 50000,
      );

      // -5% rounded to the nearest 50000
      where = addWhereClause(
        where,
        ['property_searches_bool_exp', 'computed_budget_lte', '_gte'],
        Math.round((listing.sale_price * 0.95) / 50000) * 50000,
      );
    }

    if (
      listing.rent_net != null &&
      listing.price_unit != null &&
      listing.property.computed_surface
    ) {
      let monthlyBudget = listing.rent_net;
      switch (listing.price_unit) {
        case 'yearly':
          monthlyBudget = listing.rent_net / 12;
          break;
        case 'm2yearly':
          monthlyBudget =
            (listing.rent_net * listing.property.computed_surface) / 12;
          break;
        case 'weekly':
          monthlyBudget = listing.rent_net * (365 / 7 / 12);
          break;
        case 'daily':
          monthlyBudget = listing.rent_net * 30;
          break;
        case 'monthly':
        default:
          // Do nothing.
          break;
      }

      // +50% rounded to the nearest 100
      where = addWhereClause(
        where,
        ['property_searches_bool_exp', 'computed_budget_lte', '_lte'],
        Math.round((monthlyBudget * 1.5) / 100) * 100,
      );

      // -10% rounded to the nearest 100
      where = addWhereClause(
        where,
        ['property_searches_bool_exp', 'computed_budget_lte', '_gte'],
        Math.round((monthlyBudget * 0.9) / 100) * 100,
      );
    }
  }

  if (
    listing.property.places != null &&
    listing.property.places.length > 0 &&
    listing.property.locality_id != null
  ) {
    // We want to retrieve all property_searches that target the listing's locality or its parent places (parent municipality, parent district, etc...).
    // We also want to retrieve all property_searches that target the surrounding localities.
    const searchPlacesIncludingParentPlaces = [
      ...listing.property.places.map(place => place.id),
      ...(extendedPlaces ?? []),
    ];
    // We want to retrieve all property_searches that target the listing's locality neighbourhoods (children places).
    const searchPlaces = [
      listing.property.locality_id,
      ...(extendedPlaces ?? []),
    ];

    where = addWhereClause(
      where,
      ['property_searches_bool_exp', 'property_searches_places', 'place'],
      {
        _or: [
          { id: { _in: searchPlacesIncludingParentPlaces } },
          { parent_places: { id: { _in: searchPlaces } } },
        ],
      },
    );
  }

  if (listing.property.property_type?.composite_type_object?.name != null) {
    where = addWhereClause(
      where,
      [
        'property_searches_bool_exp',
        'property_types',
        'property_type',
        'composite_type',
        '_in',
      ],
      [listing.property.property_type.composite_type_object.name],
    );
  }

  where = addWhereClause(
    where,
    ['property_searches_bool_exp', 'notification', '_in'],
    ['daily', 'weekly'],
  );

  return where;
};

type BuyerLeadMatchingFiltersInput = DefaultBuyersMatchingFiltersInput & {
  auto_matching_filters?: Property_Searches_Bool_Exp | null;
};

export const buildSearchParamsFilters = (
  listing: BuyerLeadMatchingFiltersInput | null | undefined,
  extendedPlaces?: string[],
  existingParams?: URLSearchParams,
  savedFilters?: Property_Searches_Bool_Exp | null,
) => {
  const searchParams = new URLSearchParams(existingParams);

  // Default to max
  searchParams.set('matching_limit', '500');

  // Priority to saved auto matching filters, then fallback to default filters.
  const matchingFilters = savedFilters ?? listing?.auto_matching_filters;
  if (matchingFilters != null && Object.keys(matchingFilters).length > 0) {
    searchParams.set('matching_where', JSON.stringify(matchingFilters));

    return searchParams;
  }

  if (listing != null) {
    searchParams.set(
      'matching_where',
      JSON.stringify(getDefaultBuyersMatchingFilters(listing, extendedPlaces)),
    );
  }

  return searchParams;
};

export const BUYERS_SEARCH_PARAMS_SCOPE = 'buyers';

export const parseBuyerLeadsWhereParam = (
  raw: string | null,
): Buyer_Leads_Bool_Exp =>
  prepareWhereClauseQuery(parseJsonParam<Record<string, unknown>>(raw, {}));

export const withCurrentBuyersWhere = (
  base: URLSearchParams,
  current: URLSearchParams,
): URLSearchParams => {
  const params = new URLSearchParams(base);
  const where = current.get(`${BUYERS_SEARCH_PARAMS_SCOPE}_where`);

  if (where == null) {
    params.delete(`${BUYERS_SEARCH_PARAMS_SCOPE}_where`);
  } else {
    params.set(`${BUYERS_SEARCH_PARAMS_SCOPE}_where`, where);
  }

  return params;
};
